/**
 * The Chat LAUNCH protocol — Chat's published contract for "open Chat with
 * this message".
 *
 * WHO WRITES IT: product surfaces outside Chat. Transcript and suggestion
 * chips, the floating Ask Heidi bar, the slide annotation input, the clinical
 * tasks panel, the evidence note overlay, the shared-session route. They stage
 * a query and the analytics attribution that goes with it, then open Chat.
 *
 * WHO DRAINS IT: `ChatInput`, and only `ChatInput`. It reads the four pending
 * atoms in one effect, calls `onSend` with the message plus the entry method
 * and chip metadata, and then resets all four. The delay is 500 ms by default —
 * enough for the sidebar to finish opening and rendering before the send — and
 * 0 ms when `pendingChatImmediateAtom` is set, which is for callers whose chat
 * panel is already open and who therefore have no sidebar animation to wait on.
 *
 * TO LAUNCH CHAT FROM PRODUCT CODE, IMPORT THIS MODULE, NEVER
 * `@/atoms/chatAtoms`. Chat is quarantined: an import of `chatAtoms.ts` from
 * outside Chat is a coupling the migration gate counts and refuses to let grow.
 * This protocol is not a Chat internal — twenty-two files outside Chat spoke it
 * before it had a home — so it is published here, outside the quarantined
 * globs, where product code and Chat can both depend on it without either side
 * forming a pair.
 *
 * The export list is RATCHETED (`publishedContracts` in
 * `scripts/monorepo-migration/seed/program.ts`): a new export here fails
 * `pnpm migration:check` until the program owner rules it in. Adding state that
 * only Chat reads is not a ruling — it belongs in `chatAtoms.ts`.
 *
 * This module imports `jotai` and `import type` from the analytics models, and
 * nothing else. `chatContract.lean.test.ts` holds that list; an import of a
 * `@/components/chat/**` module here would drag Chat's type graph — and through
 * it the API layer — into every product surface that stages a query.
 *
 * No re-export is left in `chatAtoms.ts`. A re-export would keep every reader
 * resolving through the one module the Chat quarantine watches by name, which
 * is the coupling this move removes.
 */

import type {
  ChipOrigin,
  ChipSurface,
  MessageEntryMethod,
} from '@heidi/evidence/contracts';
import { atom } from 'jotai';

// Atom to trigger focus on the chat input (increment to trigger focus)
export const chatInputFocusTriggerAtom = atom<number>(0);

// Atom to store a pending message to auto-send when the chat input mounts
// Used by chip clicks to send queries automatically after opening sidebar
export const pendingChatMessageAtom = atom<string | null>(null);

// Atom to store the entry method for the pending message (for analytics)
// Set alongside pendingChatMessageAtom, cleared after send
export const pendingEntryMethodAtom = atom<MessageEntryMethod | null>(null);

// Chip metadata for analytics enrichment on message_received. Set alongside
// pendingEntryMethodAtom when a shortcut/suggestion chip is clicked.
// See ChipOrigin / ChipSurface for the bucketing semantics.
export const pendingChipMetaAtom = atom<{
  label?: string;
  category?: string;
  origin?: ChipOrigin;
  surface?: ChipSurface;
} | null>(null);

// When true, the pending message is sent immediately (0ms delay) instead of
// the default 500ms. Used by SlideAnnotationInput where the chat is already
// open and no sidebar-open delay is needed.
export const pendingChatImmediateAtom = atom<boolean>(false);

// Atom to store temp user id
export const isAnonymousChatAtom = atom<boolean>(false);
